import React, { useState, useEffect, useMemo } from "react";

import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { isAuthenticated, getUserProfile } from './authService';
import { resolveProductCategory, getDepartments, getSubCategories } from '../utils/categoryMapper';
import { PUBLIC_SITE_URL } from '../App';

export default function ProductCatalog({
  products = [],
  cart = [],
  setCart,
  searchQuery,
  setSearchQuery,
  category,
  setCategory,
  sortPrice,
  setSortPrice,
}) {
  const navigate = useNavigate();

  const getCartQty = (product) => {
    const pid = product._id || product.id;
    const line = cart.find((c) => (c._id || c.id) === pid);
    return line ? (Number(line.quantity) || 0) : 0;
  };
  const setCartQty = (product, newQty) => {
    const pid = product._id || product.id;
    if (newQty <= 0) { setCart(cart.filter((c) => (c._id || c.id) !== pid)); return; }
    const idx = cart.findIndex((c) => (c._id || c.id) === pid);
    if (idx >= 0) { const updated = [...cart]; updated[idx] = { ...updated[idx], quantity: newQty }; setCart(updated); }
    else { setCart([...cart, { ...product, quantity: newQty }]); }
  };
  const changeCartQty = (product, delta) => {
    const idx = cart.findIndex((c) => c._id === product._id);
    if (idx < 0) return;
    const updated = [...cart];
    const newQty = (Number(updated[idx].quantity) || 0) + delta;
    if (newQty <= 0) { updated.splice(idx, 1); } else { updated[idx] = { ...updated[idx], quantity: newQty }; }
    setCart(updated);
  };
  const departments = getDepartments();
  const [selectedBrandPillar, setSelectedBrandPillar] = useState('executives');
  
  // Advanced filter states
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  
  // High-performance memoized B2B catalog filtration system
  const filteredProducts = useMemo(() => {
    let result = Array.isArray(products) ? [...products] : [];

    if (searchQuery && searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => 
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.brand && p.brand.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        (p.industrial_sector && p.industrial_sector.toLowerCase().includes(q))
      );
    }

    if (category && category !== "All") {
      result = result.filter(p => p.industrial_sector === category);
    }

    if (minPrice && minPrice.trim() !== '') {
      const minVal = parseFloat(String(minPrice).replace(/,/g, ''));
      if (!isNaN(minVal)) {
        result = result.filter(p => (p.priceRetail || 0) >= minVal);
      }
    }
    if (maxPrice && maxPrice.trim() !== '') {
      const maxVal = parseFloat(String(maxPrice).replace(/,/g, ''));
      if (!isNaN(maxVal)) {
        result = result.filter(p => (p.priceRetail || 0) <= maxVal);
      }
    }

    if (selectedBrand && selectedBrand !== "All") {
      result = result.filter(p => p.brand === selectedBrand);
    }

    if (sortPrice === "low-to-high") {
      result.sort((a, b) => (a.priceRetail || 0) - (b.priceRetail || 0));
    } else if (sortPrice === "high-to-low") {
      result.sort((a, b) => (b.priceRetail || 0) - (a.priceRetail || 0));
    }

    return result;
  }, [products, searchQuery, category, minPrice, maxPrice, selectedBrand, sortPrice]);

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Dynamic responsive matrices alignment matching Jumia standards
  const maxRowsPerPage = isMobile ? 6 : 10;
  const maxColsPerRow = isMobile ? 6 : 4;
  const productsPerPage = maxRowsPerPage * maxColsPerRow;

  // Safe fallback initialization to guarantee paginatedProducts is never undefined
  const totalPages = Math.max(1, Math.ceil((typeof filteredProducts !== "undefined" ? filteredProducts.length : products.length) / productsPerPage));
  const startIndex = (currentPage - 1) * productsPerPage;
  const paginatedProducts = (typeof filteredProducts !== "undefined" ? filteredProducts : products).slice(startIndex, startIndex + productsPerPage);

  // Chunk items into clean responsive rows on the fly
  const chunks = [];
  for (let i = 0; i < paginatedProducts.length; i += maxColsPerRow) {
    chunks.push(paginatedProducts.slice(i, i + maxColsPerRow));
  }

  // Per-card typed quantity (keyed by product._id)
  const [cardQuantities, setCardQuantities] = useState({});

  // Reset pagination when filters change
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setCurrentPage(1);
  };

  const handleMinPriceChange = (value) => {
    const digits = value.replace(/[^\d]/g, '');
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setMinPrice(formatted);
    setCurrentPage(1);
  };

  const handleMaxPriceChange = (value) => {
    const digits = value.replace(/[^\d]/g, '');
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setMaxPrice(formatted);
    setCurrentPage(1);
  };

  const handleBrandChange = (value) => {
    setSelectedBrand(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortPrice(value);
    setCurrentPage(1);
  };

  // Review modal states
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProductForReview, setSelectedProductForReview] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewError, setReviewError] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const baseUrl = 'https://ebakasi.onrender.com';

  // Fetch reviews for a product
  const fetchReviews = async (productId) => {
    try {
      const res = await fetch(`${baseUrl}/api/reviews/${productId}`);
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
      setReviews([]);
    }
  };

  // Open review modal
  const openReviewModal = (product) => {
    setSelectedProductForReview(product);
    setReviewForm({ rating: 5, comment: '' });
    setReviewError('');
    fetchReviews(product._id);
    setShowReviewModal(true);
  };

  // Submit review
  const submitReview = async () => {
    if (!isAuthenticated()) {
      setReviewError('Please log in to submit a review.');
      return;
    }

    setReviewLoading(true);
    setReviewError('');

    try {
      const token = localStorage.getItem('ebakasiJwtToken');
      const res = await fetch(`${baseUrl}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          productId: selectedProductForReview._id,
          rating: reviewForm.rating,
          comment: reviewForm.comment,
        }),
      });

      const data = await res.json();

      if (res.status === 403) {
        setReviewError(data.error || 'You can only review products from a delivered order.');
      } else if (!res.ok) {
        setReviewError(data.error || 'Failed to submit review.');
      } else {
        // Success - refresh reviews and close modal
        await fetchReviews(selectedProductForReview._id);
        setReviewForm({ rating: 5, comment: '' });
        setShowReviewModal(false);
        // Trigger parent refresh to update product aggregates
        if (window.location.reload) {
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Review submission error:', err);
      setReviewError('Failed to submit review. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  // 🌐 AUTOMATED CLOUDINARY ACCOUNT MATRIX COEFFICIENT
  // Frontend and backend must use the same Cloudinary cloud account for consistent image handling
  // Frontend: REACT_APP_CLOUDINARY_CLOUD_NAME (env var) or fallback to 'dlwqf6jm'
  // Backend: CLOUDINARY_CLOUD_NAME (env var) in backend/routes/products.js
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dlwqf6jm';

  // Generate inline SVG placeholder with product name (guaranteed to render without external dependencies)
  const generatePlaceholderSVG = (productName) => {
    const encodedName = encodeURIComponent(productName || 'Product');
    return `data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='480' height='360' viewBox='0 0 480 360'%3E%3Crect width='480' height='360' fill='%231e293b'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='20' font-weight='bold' fill='white'%3E${encodedName}%3C/text%3E%3C/svg%3E`;
  };

  // Helper function to render a single product card
  function renderProductCard(product) {
    // Calculate display price based on pricing tiers
    let displayPrice = Number(product.priceRetail) || 0;
    let priceLabel = 'UGX ';
    
    if (product.pricingTiers && Array.isArray(product.pricingTiers) && product.pricingTiers.length > 0) {
      const lowestTierPrice = Math.min(...product.pricingTiers.map(t => Number(t.unitPrice)));
      displayPrice = lowestTierPrice || displayPrice;
      priceLabel = 'From UGX ';
    }
    
    const cleanRenderPrice = displayPrice > 0 ? displayPrice : 38000;

    // Calculate bulk-tier saving percentage
    let dynamicSavingPercentageFraction = 0;
    if (product.pricingTiers && Array.isArray(product.pricingTiers) && product.pricingTiers.length > 0) {
      const tierPrices = product.pricingTiers.map(t => Number(t.unitPrice));
      const lowestTierPrice = Math.min(...tierPrices);
      
      // Determine base reference price: use priceRetail if present and > 0, otherwise highest tier price
      let baseReferencePrice = 0;
      if (product.priceRetail && product.priceRetail > 0) {
        baseReferencePrice = Number(product.priceRetail);
      } else {
        baseReferencePrice = Math.max(...tierPrices);
      }
      
      // Calculate percentage saved if both values are valid and base > lowest
      if (baseReferencePrice > 0 && lowestTierPrice > 0 && baseReferencePrice > lowestTierPrice) {
        dynamicSavingPercentageFraction = Math.round(((baseReferencePrice - lowestTierPrice) / baseReferencePrice) * 100);
      }
    }

    // 🚀 HIGH-PERFORMANCE DIRECT MEDIA SOURCE RECONCILIATION
    // Primary: use product image if available
    // Fallback: use inline SVG placeholder (guaranteed to render without external dependencies)
    const finalResolvedImageSrc =
      (product.image && product.image.trim() !== "")
        ? product.image
        : (product.images && product.images.length > 0 && product.images[0] && product.images[0].trim() !== "")
          ? product.images[0]
          : generatePlaceholderSVG(product.name);

    return (
      <div
        key={product._id}
        className="product-item-card"
        onClick={() => navigate(product.slug ? `/products/${product.slug}` : `/product/${product._id}`)}
        style={{ cursor: 'pointer' }}
      >
        <div 
          className="asset-box-visual"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={finalResolvedImageSrc}
            alt={product.name}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            onError={(e) => {
              // Fall back to guaranteed-safe inline SVG placeholder
              // Clear handler to prevent infinite loop
              e.target.onerror = null;
              e.target.src = generatePlaceholderSVG(product.name);
            }}
          />
        </div>

        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              className="product-main-title"
              style={{ color: 'var(--text-primary)' }}
            >
              {product.name}
            </h3>
            <div className="spec-chip-box">
              <span style={{ color: 'var(--text-secondary)' }}>
                Brand: {product.brand || 'Unbranded'}
              </span>
              {product.sku && (
                <span style={{ color: '#64748b', fontSize: '10px' }}>
                  SKU: {product.sku}
                </span>
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '8px',
                gap: '2px',
              }}
            >
              {product.pricingTiers && Array.isArray(product.pricingTiers) && product.pricingTiers.length > 0 ? (
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {product.pricingTiers.map((tier, idx) => {
                    const maxQtyDisplay = tier.maxQty ? tier.maxQty : '+';
                    return (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: idx < product.pricingTiers.length - 1 ? '1px solid #334155' : 'none' }}>
                        <span>{tier.minQty}-{maxQtyDisplay}: </span>
                        <span style={{ color: '#22c55e', fontWeight: '600' }}>UGX {Number(tier.unitPrice).toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="price-display-text">
                  {priceLabel}{cleanRenderPrice.toLocaleString()}
                </span>
              )}

            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>  
              {getCartQty(product) > 0 ? (  
                <div style={{ display: "flex", gap: "8px", alignItems: "center", width: "100%" }}>  
                  <button  
                    className="interactive-btn"  
                    onClick={(e) => { e.stopPropagation(); changeCartQty(product, -1); }}  
                    style={{ width: "40px", padding: "8px", borderRadius: "6px" }}  
                  >  
                    −  
                  </button>  
                  <span style={{ flex: 1, textAlign: "center", fontWeight: 700, color: "#16a34a" }}>  
                    ✓ {getCartQty(product)} Selected  
                  </span>  
                  <button  
                    className="interactive-btn"  
                    onClick={(e) => { e.stopPropagation(); changeCartQty(product, 1); }}  
                    style={{ width: "40px", padding: "8px", borderRadius: "6px" }}  
                  >  
                    +  
                  </button>  
                </div>  
              ) : (  
                <>  
                  <input  
                    type="number"  
                    min="1"  
                    value={cardQuantities[product._id] === undefined ? "" : cardQuantities[product._id]}  
                    onClick={(e) => e.stopPropagation()}  
                    onChange={(e) => {  
                      e.stopPropagation();  
                      const raw = e.target.value; const val = raw === "" ? "" : Math.max(1, parseInt(raw, 10) || 1);  
                      setCardQuantities((prev) => ({ ...prev, [product._id]: val }));  
                    }}  
                    style={{  
                      width: "56px",  
                      padding: "8px",  
                      borderRadius: "6px",  
                      border: "1px solid #334155",  
                      background: "#0f172a",  
                      color: "#fff",  
                      textAlign: "center",  
                    }}  
                  />  
                  <button  
                    className="interactive-btn"  
                    onClick={(e) => {  
                      e.stopPropagation();  
                      const qtyToAdd = Math.max(1, parseInt(cardQuantities[product._id], 10) || 1);  
                      const existingIndex = cart.findIndex((c) => c._id === product._id);  
                      if (existingIndex >= 0) {  
                        const updated = [...cart];  
                        updated[existingIndex] = {  
                          ...updated[existingIndex],  
                          quantity: (Number(updated[existingIndex].quantity) || 0) + qtyToAdd,  
                        };  
                        setCart(updated);  
                      } else {  
                        setCart([...cart, { ...product, quantity: qtyToAdd, priceRetail: cleanRenderPrice }]);  
                      }  
                      setCardQuantities((prev) => ({ ...prev, [product._id]: "" }));  
                    }}  
                  >  
                    Add to Cart  
                  </button>  
                </>  
              )}  
            </div>
          </div>
        </div>
      </div>
    );
  }

  const platformPillarsDataLedger = {
    executives: {
      title: '💼 CORPORATE OPERATIONS & DEFENSE MATRICES',
      subtitle:
        'Direct Factory Integrations • Multi-Million Shilling Procurement Safeguards',
      text: 'Ebakasi serves as the ultimate procurement defense layer for corporate executives and infrastructure directors across East Africa. We bypass middleman markup layers entirely by maintaining direct, absolute alliances with processing manufacturing plants both here in Uganda and overseas. To secure your bottom-line capital deployments, our architecture isolates financial channels using real-time escrow allocations, preventing supply-chain deficits and logistics errors before they hit your physical construction site yard paths.',
    },
    engineers: {
      title: '👷 CERTIFIED PROFESSIONAL FIELD ENGINEERS DESK',
      subtitle:
        'Promoting Local Architectural Skills • Expanding Institutional Value Networks',
      text: 'To the civil designers, electrical grid managers, plumbing system specialists, and mechanized agricultural officers of Uganda: your expertise is our blueprint. Ebakasi treats you as the backbone of our sovereign infrastructure. We provide a state-of-the-art public directory layout explicitly designed to promote your specialized capabilities, connect you directly with enterprise site owners, and increase your contract pipeline.',
    },
    vendors: {
      title: '🏭 DIRECT MANUFACTURING PROSPERITY ENGINE',
      subtitle:
        'Accelerating Sales Volumes • Optimizing Production Floor Deliveries',
      text: 'To our verified factory partners and commercial supply merchants: Ebakasi is engineered to propel your turnover to its absolute zenith. By digitizing old storefront channels, our system opens up direct high-velocity transaction links with corporate procurement offices globally.',
    },
  };
  return (
    <>
      <Helmet>
        <title>Ebakasi Industrial Uganda | B2B Industrial Procurement Platform</title>
        <meta name="description" content="Ebakasi Industrial Uganda - East Africa's premier B2B industrial procurement platform. Direct factory sourcing for construction materials, electrical supplies, plumbing, agricultural equipment, and industrial machinery. Secure escrow payments, verified suppliers, and nationwide delivery across Uganda." />
        <link rel="canonical" href={`${PUBLIC_SITE_URL}/`} />
      </Helmet>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '5px 5px 60px 5px' }}>
      {/* INJECTING PERMANENT ANTI-BLINK ASPECT RATIO STYLE SHEET OVERRIDES */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
                .catalog-section-container { display: flex; flex-direction: column; gap: 30px; }
                
                                
.product-grid { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }  
                .product-row {  
                    display: flex;  
                    overflow-x: auto;  
                    overflow-y: hidden;  
                    gap: 8px;  
                    scroll-snap-type: x mandatory;  
                    -webkit-overflow-scrolling: touch;  
                    scrollbar-width: none;  
                    padding-bottom: 4px;  
                }  
                .product-row::-webkit-scrollbar { display: none; }  
                .product-cell {  
                    flex: 0 0 auto;  
                    width: calc(40% - 6px);  
                    scroll-snap-align: start;  
                    box-sizing: border-box;  
                }  
                .product-cell img, .product-cell [class*="image"], .product-cell .asset-box-visual {  
                    height: 120px !important;  
                    max-height: 120px !important;  
                    width: 100% !important;  
                    object-fit: cover !important;  
                }  
                @media (min-width: 768px) {  
                    .product-row {  
                        display: grid;  
                        grid-template-columns: repeat(4, 1fr);  
                        gap: 16px;  
                        overflow-x: visible;  
                        overflow-y: visible;  
                    }  
                    .product-cell { width: auto; }  
                    .product-cell img, .product-cell [class*="image"], .product-cell .asset-box-visual {  
                        height: 150px !important;  
                        max-height: 150px !important;  
                    }  
                }


                .product-shelf {
                    display: flex;
                    overflow-x: auto;
                    gap: 12px;
                    scroll-snap-type: x mandatory;
                    -webkit-overflow-scrolling: touch;
                    padding-bottom: 8px;
                }

                .product-shelf::-webkit-scrollbar {
                    display: none;
                }

                .product-shelf .product-item-card {
                    flex: 0 0 auto;
                    width: 45%;
                    scroll-snap-align: start;
                }

                @media (min-width: 480px) {
                    .product-shelf .product-item-card {
                        width: 35%;
                    }
                }

                @media (min-width: 768px) {
                    .product-shelf .product-item-card {
                        width: 25%;
                    }
                }

                @media (min-width: 1024px) {
                    .product-shelf .product-item-card {
                        width: 20%;
                    }
                }
                
                .product-item-card { 
                    background: var(--bg-card); border: 1px solid var(--border-line); border-radius: 10px; padding: 10px; display: flex; flex-direction: column; justify-content: space-between; gap: 8px; box-sizing: border-box; transition: all 0.3s ease; cursor: pointer;
                }
                @media (hover: hover) {
                    .product-item-card:hover {
                        transform: translateY(-4px); border-color: var(--accent-shout); box-shadow: 0 12px 30px rgba(0,0,0,0.4);
                    }
                }
                .interactive-btn {
                    padding: 8px 0 !important; font-size: 12px !important; font-weight: 800 !important; letter-spacing: 0.3px; border: 2px solid var(--accent-shout) !important; border-radius: 8px !important; background-color: var(--accent-shout) !important; color: #fff !important; cursor: pointer; transition: all 0.25s ease; text-transform: uppercase;
                }
                
                /* FIX: HARDENS PILLAR TAB SELECTION INTERACTIVITY LOGIC DISPLAY TRACKS */
                .pillar-tab-trigger {
                    padding: 10px 18px; background-color: var(--bg-main); border: 1px solid var(--border-line); color: var(--text-secondary); border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.2s ease;
                }
                .pillar-tab-trigger:hover {
                    background-color: var(--bg-card); color: var(--text-primary); border-color: var(--border-line);
                }
                .pillar-tab-trigger.active {
                    background-color: var(--accent-shout) !important; color: var(--text-primary) !important; border-color: var(--accent-shout) !important; box-shadow: 0 4px 12px rgba(234,88,12,0.3);
                }
                
                .asset-box-visual { 
                    width: 100%; height: 110px; background-color: var(--bg-main); border-radius: 6px; display: flex; justify-content: center; align-items: center; overflow: hidden; position: relative; aspect-ratio: 4/3;
                }
                .product-main-title { margin: 0 0 4px 0; font-size: 13px; font-weight: 700; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 32px; line-height: 1.3; color: var(--text-primary, #fff); }
                .spec-chip-box { display: flex; flex-direction: column; gap: 2px; font-size: 10px; background: var(--bg-main); padding: 4px; border-radius: 4px; margin-bottom: 6px; color: var(--text-secondary); }
                .price-display-text { font-size: 14px; font-weight: 800; color: var(--accent-shout); }

                @media (min-width: 1024px) {
                    .product-item-card { padding: 20px !important; border-radius: 14px !important; }
                    .asset-box-visual { height: 165px !important; }
                    .product-main-title { font-size: 16px !important; -webkit-line-clamp: unset !important; min-height: auto !important; margin-bottom: 10px !important; line-height: 1.4 !important; }
                    .spec-chip-box { font-size: 11px !important; gap: 6px !important; padding: 12px !important; border-radius: 6px !important; margin-bottom: 12px !important; }
                    .price-display-text { font-size: 18px !important; }
                }

                /* Mobile hero compact styling */
                @media (max-width: 768px) {
                    .corporate-hero-card {
                        padding: 12px !important;
                        margin-bottom: 12px !important;
                    }
                    .corporate-hero-card h1 {
                        font-size: 17px !important;
                        margin-bottom: 6px !important;
                    }
                    .corporate-hero-card p {
                        font-size: 12px !important;
                        margin-bottom: 0 !important;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                    }

                    /* Ultra-compact mobile product cards */
                    .product-item-card {
                        padding: 6px !important;
                        gap: 4px !important;
                    }
                    .asset-box-visual {
                        height: 80px !important;
                    }
                    .product-main-title {
                        font-size: 11px !important;
                        min-height: 24px !important;
                        margin-bottom: 2px !important;
                    }
                    .spec-chip-box {
                        font-size: 8px !important;
                        gap: 1px !important;
                        padding: 2px !important;
                        margin-bottom: 2px !important;
                    }

                    /* Fix mobile filter input overflow */
                    .filter-bar-container {
                        flex-direction: column !important;
                        align-items: stretch !important;
                    }
                    .filter-bar-container span {
                        width: 100%;
                        margin-bottom: 8px;
                    }
                    .filter-bar-container .controls-row {
                        flex-direction: column !important;
                        align-items: stretch !important;
                        width: 100% !important;
                    }
                    .filter-bar-container .controls-row.filter-inputs-container {
                        flex-direction: column !important;
                        width: 100% !important;
                    }
                    .filter-bar-container input,
                    .filter-bar-container select {
                        width: 100% !important;
                        min-width: 0 !important;
                        box-sizing: border-box !important;
                        max-width: 100% !important;
                    }
                    .filter-bar-container .price-inputs-row {
                        display: flex;
                        gap: 8px;
                        width: 100%;
                    }
                    .filter-bar-container .price-inputs-row input {
                        flex: 1;
                        min-width: 0 !important;
                        box-sizing: border-box !important;
                    }
                    .price-display-text {
                        font-size: 13px !important;
                    }
                    .interactive-btn {
                        padding: 6px 0 !important;
                        font-size: 11px !important;
                    }
                }
                
                /* Pagination Controls */
                .pagination-controls {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 8px;
                    margin-top: 30px;
                    flex-wrap: wrap;
                }
                
                .pagination-btn {
                    padding: 8px 16px;
                    background-color: var(--bg-main);
                    border: 1px solid var(--border-line);
                    color: var(--text-primary, #fff);
                    border-radius: 6px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    min-width: 40px;
                }
                
                .pagination-btn:hover {
                    background-color: var(--bg-card);
                    border-color: var(--border-line, #475569);
                }
                
                .pagination-btn.active {
                    background-color: var(--accent-shout);
                    border-color: var(--accent-shout);
                }
                
                .pagination-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            `,
        }}
      />
      {/* SOVEREIGN INDUSTRIAL BANNER HERO GRID */}
      <div
        className="corporate-hero-card"
        style={{
          borderLeft: '5px solid var(--accent-shout)',
          borderRadius: '16px',
          padding: '16px 20px',
          marginBottom: '16px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          backgroundColor: 'var(--bg-card)',
        }}
      >
        <h1
          style={{
            margin: '0 0 8px 0',
            fontSize: '20px',
            fontWeight: '900',
            letterSpacing: '-0.5px',
            lineHeight: '1.2',
            color: 'var(--text-primary)',
          }}
        >
          UGANDA'S INDUSTRIAL BACKBONE
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: 'var(--text-secondary)',
            lineHeight: '1.5',
            maxWidth: '900px',
          }}
        >
          Direct industrial procurement connecting East African enterprises, contractors, and builders to global manufacturing floors.
        </p>
        <p
          style={{
            margin: '8px 0 0 0',
            fontSize: '13px',
            fontWeight: '800',
            color: 'var(--accent-shout)',
            lineHeight: '1.5',
            maxWidth: '900px',
          }}
        >
          YOUR PRODUCTS. EVERYWHERE. YOUR PAYMENTS GUARANTEED.
        </p>
      </div>

      <div
        style={{
          background: 'linear-gradient(135deg, var(--border-line) 0%, var(--bg-card) 100%)',
          borderLeft: '4px solid var(--accent-shout)',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '25px',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '15px',
            fontWeight: '800',
            color: 'var(--text-primary)',
          }}
        >
          🧱 EBAKASI INDUSTRIAL UGANDA PROCUREMENTS DIRECTORY
        </h3>
        <div
          className="filter-bar-container"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Secure escrow protection: You maintain complete control of your capital. Funds are only released to the vendor once you inspect and verify your delivered industrial cargo.
          </span>
          <div className="controls-row filter-inputs-container" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '4px',
                fontSize: '12px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <div className="price-inputs-row">
              <input
                type="text"
                inputMode="numeric"
                placeholder="Min Price"
                value={minPrice}
                onChange={(e) => handleMinPriceChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <input
                type="text"
                inputMode="numeric"
                placeholder="Max Price"
                value={maxPrice}
                onChange={(e) => handleMaxPriceChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--bg-main)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-line)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '4px',
                fontSize: '12px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            >
              <option value="">All Manufacturers</option>
              {[...new Set(products.map(p => p.brand).filter(Boolean))].sort().map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
            <select
              value={sortPrice}
              onChange={(e) => handleSortChange(e.target.value)}
              style={{
                padding: '8px 12px',
                backgroundColor: 'var(--bg-main)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-line)',
                borderRadius: '4px',
                fontSize: '12px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            >
              <option value="newest">Sort by: Default Chronological</option>
              <option value="low-to-high">
                Price: Low to High
              </option>
              <option value="high-to-low">
                Price: High to Low
              </option>
            </select>
          </div>
        </div>
      </div>

      <div className="catalog-section-container">
        {paginatedProducts.length === 0 ? (
          <div className="text-center py-12 text-slate-400 font-medium">No premium industrial assets found matching criteria.</div>
        ) : (
          <div className="product-grid">
            {chunks.map((row, ri) => (
              <div className="product-row" key={`row-${ri}`}>
                {row.map((product) => (
                  <div key={product._id || product.id} className="product-cell">
                    {renderProductCard(product)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 🔒 TRUST & SECURITY SECTION */}
      <div
        style={{
          background: 'linear-gradient(135deg, var(--bg-card) 0%, var(--bg-main) 100%)',
          border: '1px solid var(--border-line)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '25px',
        }}
      >
        <h3
          style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            fontWeight: '800',
            color: '#22c55e',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          🛡️ TRUST & SECURITY GUARANTEES
        </h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
          }}
        >
          <div
            style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid #22c55e',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔐</div>
            <h4
              style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '700',
                color: 'var(--text-primary)',
              }}
            >
              256-Bit Escrow Protection
            </h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              All payments are held in secure escrow until you confirm satisfactory delivery. Your funds are protected against fraud and non-delivery.
            </p>
          </div>
          <div
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
            <h4
              style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '700',
                color: 'var(--text-primary)',
              }}
            >
              Verified Vendors Only
            </h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Every supplier undergoes rigorous verification including business registration, physical location verification, and quality assurance checks.
            </p>
          </div>
          <div
            style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid var(--accent-shout)',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>🏭</div>
            <h4
              style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '700',
                color: 'var(--text-primary)',
              }}
            >
              Direct Factory Sourcing
            </h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              We connect you directly with manufacturers and authorized distributors, eliminating middlemen and ensuring authentic products at factory prices.
            </p>
          </div>
          <div
            style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              padding: '16px',
            }}
          >
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>📦</div>
            <h4
              style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: '700',
                color: 'var(--text-primary)',
              }}
            >
              Quality Assurance
            </h4>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              All products meet international quality standards. We offer dispute resolution and replacement guarantees for defective or non-compliant items.
            </p>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedProductForReview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--bg-card)',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              border: '1px solid var(--border-line)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>
                Reviews: {selectedProductForReview.name}
              </h3>
              <button
                onClick={() => setShowReviewModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-primary)',
                  fontSize: '24px',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            {/* Review Form */}
            {isAuthenticated() && (
              <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid var(--border-line)' }}>
                <h4 style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Write a Review
                </h4>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                    Rating:
                  </label>
                  <select
                    value={reviewForm.rating}
                    onChange={(e) => setReviewForm({ ...reviewForm, rating: parseInt(e.target.value) })}
                    style={{
                      padding: '8px',
                      backgroundColor: 'var(--bg-main)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-line)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      width: '100%',
                    }}
                  >
                    <option value={5}>★ 5 - Excellent</option>
                    <option value={4}>★ 4 - Good</option>
                    <option value={3}>★ 3 - Average</option>
                    <option value={2}>★ 2 - Poor</option>
                    <option value={1}>★ 1 - Terrible</option>
                  </select>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                    Comment:
                  </label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    placeholder="Share your experience with this product..."
                    style={{
                      padding: '8px',
                      backgroundColor: 'var(--bg-main)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-line)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      width: '100%',
                      minHeight: '80px',
                      resize: 'vertical',
                    }}
                  />
                </div>
                {reviewError && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '8px' }}>
                    {reviewError}
                  </div>
                )}
                <button
                  onClick={submitReview}
                  disabled={reviewLoading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--accent-shout)',
                    color: 'var(--bg-main)',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: reviewLoading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {reviewLoading ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            )}

            {/* Reviews List */}
            <div>
              <h4 style={{ color: 'var(--text-secondary)', marginBottom: '12px' }}>
                Customer Reviews ({reviews.length})
              </h4>
              {reviews.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: '12px' }}>
                  No reviews yet. Be the first to review!
                </p>
              ) : (
                reviews.map((review) => (
                  <div
                    key={review._id}
                    style={{
                      backgroundColor: 'var(--bg-main)',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: '1px solid var(--border-line)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600' }}>
                          {review.buyerName || 'Anonymous'}
                        </span>
                        <span style={{ color: '#22c55e', fontSize: '10px', fontWeight: '600' }}>
                          ✓ Verified Purchase
                        </span>
                      </div>
                      <span style={{ color: 'var(--accent-shout)', fontSize: '12px' }}>
                        {'★'.repeat(review.rating)}
                      </span>
                    </div>
                    {review.comment && (
                      <p style={{ color: 'var(--text-secondary)', fontSize: '12px', margin: '4px 0 0 0' }}>
                        {review.comment}
                      </p>
                    )}
                    <span style={{ color: '#64748b', fontSize: '10px' }}>
                      {new Date(review.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
